import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export type LegalDocKey = 'privacy' | 'terms';
interface LegalSection {
  heading: string;
  body: string[];
}
interface LegalDoc {
  title: string;
  updated: string;
  intro: string;
  sections: LegalSection[];
}
const DOCS: Record<LegalDocKey, LegalDoc> = {
  privacy: {
    title: 'Privacy Policy',
    updated: 'Last updated June 2026',
    intro: 'At NÍTIDO PRO, your privacy matters. This policy explains what information we collect when you request a quote or use our website, and how we use and protect it.',
    sections: [{
      heading: '1. Information We Collect',
      body: ['When you submit a quote request, we collect details you provide such as your name, phone number, email address, service city, property details, and any notes you choose to share.', 'We may also collect basic technical information automatically, such as your browser type and the pages you view, to help us improve the site experience.']
    }, {
      heading: '2. How We Use Your Information',
      body: ['We use your information solely to prepare and deliver your cleaning quote, schedule and perform services, and communicate with you about your request.', 'We do not sell, rent, or trade your personal information to third parties.']
    }, {
      heading: '3. Messaging & WhatsApp',
      body: ['When you choose to send your request through WhatsApp, the details you submit are transmitted via WhatsApp under their own terms and privacy practices.']
    }, {
      heading: '4. Data Retention & Security',
      body: ['We keep your information only as long as needed to provide our services and meet legal obligations, and we apply reasonable safeguards to protect it from unauthorized access.']
    }, {
      heading: '5. Your Choices',
      body: ['You may request that we update or delete the personal information we hold about you at any time.']
    }]
  },
  terms: {
    title: 'Terms of Service',
    updated: 'Last updated June 2026',
    intro: 'These terms govern your use of the NÍTIDO PRO website and the cleaning services we provide. By requesting a quote or booking a service, you agree to the terms below.',
    sections: [{
      heading: '1. Services',
      body: ['NÍTIDO PRO provides residential and commercial cleaning services, including move-in/move-out, deep, post-construction, commercial, and Airbnb turnover cleaning.', 'Quotes are estimates based on the information you provide. Final pricing may vary depending on the actual size, condition, and scope of the space.']
    }, {
      heading: '2. Quotes & Booking',
      body: ['Submitting a quote request does not create a confirmed booking. A service is scheduled only once we confirm a date, time, and agreed price with you.']
    }, {
      heading: '3. Scheduling & Access',
      body: ['You are responsible for providing safe and timely access to the property. Delays caused by lack of access may affect scheduling or pricing.']
    }, {
      heading: '4. Satisfaction Guarantee',
      body: ['If you are not satisfied with a completed clean, notify us promptly and we will return to address the areas in question, subject to our guarantee policy.']
    }, {
      heading: '5. Limitation of Liability',
      body: ['To the fullest extent permitted by law, NÍTIDO PRO is not liable for indirect or incidental damages arising from the use of our website or services.']
    }, {
      heading: '6. Changes to These Terms',
      body: ['We may update these terms from time to time. Continued use of our services after changes take effect constitutes acceptance of the revised terms.']
    }]
  }
};
interface LegalModalProps {
  doc: LegalDocKey | null;
  onClose: () => void;
}
export function LegalModal({
  doc,
  onClose
}: LegalModalProps) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (doc) {
      document.addEventListener('keydown', handleKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [doc, onClose]);
  const content = doc ? DOCS[doc] : null;
  return <AnimatePresence data-id="element-127">
      {content && <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25
    }} data-id="element-128">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-ink/80 backdrop-blur-sm" onClick={onClose} aria-hidden="true" data-id="element-129"></div>

          {/* Panel */}
          <motion.div role="dialog" aria-modal="true" aria-labelledby="legal-modal-title" className="relative w-full max-w-2xl max-h-[85vh] bg-graphite border border-concrete/10 shadow-2xl flex flex-col" initial={{
        opacity: 0,
        y: 24,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 24,
        scale: 0.98
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} data-id="element-130">
            {/* Header */}
            <div className="flex items-start justify-between gap-6 px-8 pt-8 pb-6 border-b border-concrete/10" data-id="element-131">
              <div data-id="element-132">
                <div className="flex items-center gap-3 mb-3" data-id="element-133">
                  <div className="w-6 h-[1px] bg-lime" data-id="element-134"></div>
                  <span className="text-concrete/50 font-body text-[10px] uppercase tracking-[0.3em]" data-id="element-135">
                    Legal
                  </span>
                </div>
                <h2 id="legal-modal-title" className="text-2xl md:text-3xl text-paper font-light" data-id="element-136">
                  {content.title}
                </h2>
                <p className="text-concrete/40 text-[11px] uppercase tracking-[0.2em] mt-2" data-id="element-137">
                  {content.updated}
                </p>
              </div>
              <button onClick={onClose} aria-label="Close" className="text-concrete/60 hover:text-lime transition-colors shrink-0 mt-1" data-id="element-138">
                <X size={22} strokeWidth={1.5} data-id="element-139" />
              </button>
            </div>

            {/* Body */}
            <div className="px-8 py-8 overflow-y-auto" data-id="element-140">
              <p className="text-concrete/70 font-light text-sm leading-relaxed mb-10" data-id="element-141">
                {content.intro}
              </p>
              <div className="space-y-8" data-id="element-142">
                {content.sections.map(section => <div key={section.heading} data-id="element-143">
                    <h3 className="text-paper font-medium text-sm tracking-wide mb-3" data-id="element-144">
                      {section.heading}
                    </h3>
                    {section.body.map((para, i) => <p key={i} className="text-concrete/60 font-light text-sm leading-relaxed mb-3 last:mb-0" data-id="element-145">
                        {para}
                      </p>)}
                  </div>)}
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}